import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  acquireGeolocation,
  type GeoFix,
  type GeoOutcome,
} from "@/lib/geolocation";
import {
  consentSnapshot,
  useConsentStore,
  type ConsentKind,
  type ConsentState,
} from "@/lib/consent";

/** Items the master checkbox grants in one shot. The list stays
 *  visible so the user can read what they're agreeing to (informed
 *  consent under GDPR Art. 7 — bundled but disclosed). Labels and
 *  explainers are resolved through i18n at render (keyed by kind). */
const ITEMS: { kind: ConsentKind }[] = [
  { kind: "join" },
  { kind: "audio" },
  { kind: "video" },
  { kind: "snapshot" },
  { kind: "recording" },
  { kind: "geolocation" },
];

const ALL_KINDS = ITEMS.map((i) => i.kind);

/** How long the consent gate will wait on a GPS fix before joining
 *  anyway. Backlog 2026-09-22 — see `handleContinue`. Short enough that
 *  a beneficiary never thinks the app froze, long enough that a phone
 *  with working location still attaches its fix inline. */
const GEO_SOFT_CAP_MS = 6_000;

type Props = {
  onProceed: (state: { consents: ConsentState; geo: GeoFix | null }) => void;
  /** Called if a GPS fix lands AFTER we already proceeded without it
   *  (see `GEO_SOFT_CAP_MS`). Lets the caller attach the coordinates to
   *  the session late rather than losing them — the join itself is
   *  never held up waiting. */
  onLateGeo?: (geo: GeoFix) => void;
  /** The org's `require_field_geo` policy. Accepted for caller
   * compatibility (JoinPage still reads it from the org and passes it
   * through) but no longer changes behavior here — see the backlog note
   * below. Kept in the signature in case a future org-specific banner
   * tone is wanted; unused for now. */
  requireGeo?: boolean;
};

// Backlog 2026-09-21 — geolocation NEVER blocks the call anymore, for
// any org. A missing/denied GPS fix used to turn the Continue button
// into a hard gate when `requireGeo` was on (the default); in practice
// that meant a beneficiary whose Chrome blocked location (common — no
// special settings needed, just a declined prompt or a locked-down
// browser policy) simply couldn't join at all. Now a failed fix shows a
// persistent red banner (so the operator/report can see geo wasn't
// captured — the report already flags "GPS non capté") and the
// beneficiary proceeds straight through regardless.
export function ConsentScreen({ onProceed, onLateGeo }: Props) {
  const { t } = useTranslation();
  const setKind = useConsentStore((s) => s.set);
  const [agreed, setAgreed] = useState(true);
  const [mediaErr, setMediaErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set whenever a geo acquire fails, for ANY reason — drives the red
  // banner. Never blocks `onProceed`. Cleared on each new attempt.
  const [geoFailure, setGeoFailure] = useState<
    (GeoOutcome & { ok: false }) | null
  >(null);

  // Pre-grant every consent in the store so a withdrawal mid-session
  // can still flip a single kind back to "denied" without first
  // having to grant it. Re-runs only when the master flips.
  useEffect(() => {
    const next = agreed ? "granted" : "denied";
    for (const kind of ALL_KINDS) setKind(kind, next);
  }, [agreed, setKind]);

  async function handleContinue() {
    setBusy(true);
    setGeoFailure(null);
    setMediaErr(null);
    let geo: GeoFix | null = null;
    // iOS Safari requires the geolocation prompt to be triggered from a
    // user gesture handler — this onClick handler IS that gesture, so
    // we call directly here, not later or behind a Promise indirection.
    //
    // Backlog 2026-09-22 — but do NOT block the join on it finishing.
    // `acquireGeolocation` is 20s per attempt with one retry, so a phone
    // whose browser silently swallows the permission prompt (location
    // off at the OS level, a locked-down policy, an ignored prompt) left
    // the beneficiary staring at a greyed-out "Acquiring location…"
    // button for a FULL 40 SECONDS with no way forward. Reproduced on
    // prod 2026-09-22 against a real stuck appointment: the beneficiary
    // never got past this screen, so redemption never fired — the
    // session showed 0 participants and `redeemed_at=NULL` while they
    // reported "black screen, nothing happens".
    //
    // Race the acquire against a short cap: a healthy device still
    // attaches its fix inline, everyone else joins within
    // GEO_SOFT_CAP_MS. The acquire keeps running in the background and
    // reports a late fix through `onLateGeo` so the coordinates aren't
    // lost — geo is optional server-side (JoinPage POSTs it only
    // `if (geo)`), so proceeding without it is always safe.
    const pending = acquireGeolocation();
    const capped = await Promise.race([
      pending,
      new Promise<"timeout">((resolve) =>
        setTimeout(() => resolve("timeout"), GEO_SOFT_CAP_MS),
      ),
    ]);

    if (capped === "timeout") {
      // Still trying — join now, attach the fix if it ever lands.
      void pending.then((late) => {
        if (late.ok) {
          setKind("geolocation", "granted");
          onLateGeo?.(late.fix);
        }
      });
    } else if (capped.ok) {
      geo = capped.fix;
      setKind("geolocation", "granted");
    } else {
      // Definitive failure inside the cap (denied / unsupported). Treat
      // denial as withdrawn consent — logged as an audit event upstream.
      // Surface the red banner, but never stop the call over it.
      setKind("geolocation", capped.code === "denied" ? "denied" : "revoked");
      setGeoFailure(capped);
    }

    // Pre-warm camera + mic permissions on this same user gesture.
    // Android Chrome enforces "getUserMedia must be on a user gesture"
    // strictly; LiveKit's `<LiveKitRoom video audio>` triggers
    // getUserMedia async after navigation, by which point the gesture
    // is gone — Android silently fails with no prompt. Calling
    // getUserMedia here, inside the click handler, makes Android
    // prompt now; LiveKit's later call inherits the grant.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      for (const track of stream.getTracks()) track.stop();
    } catch (e) {
      setMediaErr((e as Error).message || t("ui.consentScreen.mediaDenied"));
    }

    setBusy(false);
    onProceed({ consents: consentSnapshot(), geo });
  }

  return (
    <div className="mx-auto max-w-lg p-6">
      <h1 className="mb-2 text-xl font-semibold text-nb-text">{t("ui.consentScreen.title")}</h1>
      <p className="mb-6 text-sm text-nb-text">
        {t("ui.consentScreen.intro")}
      </p>

      <div className="card border-nb-border">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            aria-label={t("ui.consentScreen.masterAria")}
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-600 bg-nb-surface text-nb-mint focus:ring-nb-mint"
          />
          <div>
            <div className="font-medium text-nb-text">
              {t("ui.consentScreen.masterLabel")}
            </div>
            <p className="mt-1 text-xs text-nb-text-2">
              {t("ui.consentScreen.masterHint")}
            </p>
          </div>
        </label>

        <ul className="mt-4 space-y-2 border-t border-nb-border pt-4">
          {ITEMS.map(({ kind }) => (
            <li key={kind} className="flex items-start gap-2 text-xs">
              <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-nb-mint" />
              <div>
                <span className="font-medium text-nb-text">{t(`ui.consentScreen.${kind}Label`)}</span>{" "}
                <span className="text-nb-text-2">— {t(`ui.consentScreen.${kind}Explainer`)}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Backlog 2026-09-21 — red, not amber: this is a real gap in the
          report (no GPS captured), so it should read as urgent — but it
          NEVER blocks the button below. The call always proceeds. */}
      {geoFailure && (
        <div
          role="alert"
          className="mt-4 rounded-md border border-cvp-danger/50 bg-cvp-danger/10 p-3"
        >
          <p className="text-sm font-medium text-cvp-danger">
            {t(
              "ui.consentScreen.locationUnavailableTitle",
              "Location not available",
            )}
          </p>
          <p className="mt-1 text-xs text-nb-text-2">
            {geoFailure.code === "denied" || geoFailure.code === "unsupported"
              ? t("ui.consentScreen.locationDeniedHelp")
              : t("ui.consentScreen.locationUnavailableHelp")}
          </p>
          <p className="mt-1 text-xs text-nb-text-2">
            {t(
              "ui.consentScreen.locationUnavailableProceedNote",
              "You can still continue — the inspection report will simply show that location wasn't captured.",
            )}
          </p>
        </div>
      )}
      {mediaErr && (
        <p role="alert" className="mt-2 text-xs text-cvp-warn">
          {t("ui.consentScreen.mediaError", { message: mediaErr })}
        </p>
      )}

      <button
        type="button"
        className="btn-primary mt-6 w-full"
        disabled={!agreed || busy}
        onClick={handleContinue}
      >
        {busy ? t("ui.consentScreen.acquiring") : t("ui.consentScreen.accept")}
      </button>
    </div>
  );
}